import { PlusOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';
import { useMount } from 'ahooks';
import { Button, message, Radio, Select, Tag } from 'antd';
import FormField from 'components/FormField';
import useModal from 'hooks/useModal';
import useSmartField from 'hooks/useSmartField';
import { useTranslate } from '@tolgee/react';
import { ItemSelectModal } from 'views/Aide/components/ItemSelect';
import { useTask } from 'views/Marketing';
import { FV } from './share';
import { DisabledProps } from '../TaskForm/share';
import useCrud from 'hooks/useCrud';
import { queryItemGroupList } from 'views/Management/ItemManage/ItemGroup/api';
import Empty from 'components/Empty';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { EditDrawer } from 'views/Management/ItemManage/ItemGroup/EditDrawer';
import { noop, uniq } from 'lodash-es';
import PreviewItemWrapper, { ItemRender } from 'views/Management/ItemManage/ItemGroup/PreviewItem';
import StatusButton from 'components/StatusButton';
import { getItemsByInternalIds } from '@knowledge/common/cache/spu';
import {
  ItemSelectV2,
  ItemSelectV2ComponentProps,
} from '@knowledge/common/components/ItemSelectV2';
import { Preview } from '@knowledge/Activity/share';
import { useOrgConfigStateValue } from 'hooks/business/useOrgConfig';
import { stores2storeScopes, useExternalStoreMap } from '../NewPolicy/StoresField';

const Container = styled.div`
  display: inline-flex;
  flex-direction: column;
  align-items: stretch;
`;

const Radios = styled.div`
  line-height: 32px;
`;

const Extra = styled.div`
  display: flex;
  align-items: center;
  height: 40px;
  padding: 0 16px;
  background: var(--ant-primary-1);
`;

export interface ItemLimitFieldProps {
  taskId?: number;
  stores?: Aide.Policy['stores'];
  skuEnable?: boolean;
  platform: General.PlatformUpperCase;
  region: General.UpperCaseRegion;
}

function ItemLimitField(props: ItemLimitFieldProps & DisabledProps) {
  const { taskId, stores, skuEnable = true, platform, region, disabled } = props;

  const { t } = useTranslate();

  const {
    field: { value: type, onChange: onTypeChange },
  } = useSmartField<FV['limitItem']['type']>('limitItem.type');

  const {
    field: { value: specificItems = [], onChange: onSpecificItemsChange },
  } = useSmartField<FV['limitItem']['specificItems']>('limitItem.specificItems');

  const validate = (value: FV['limitItem']) => {
    const { type, specificItems = [], itemGroupIds = [] } = value;

    if (type === 'ITEM_GROUP' && itemGroupIds.length === 0) {
      return t('department.请选择x', { x: t('商品组') });
    }

    if (type === 'SPECIFIC' && specificItems.length === 0) {
      return t('aide.请添加商品');
    }

    return undefined;
  };

  const preOrderTypeChecked = type === 'PRE_ORDER' || type === 'IN_STOCK';

  const { visible, show, hide, meta } = useModal(false, specificItems);

  const { item: task, fetch } = useTask();

  useMount(() => {
    taskId &&
      fetch(taskId).catch(() => {
        message.error(t('phrase.获取数据失败，请稍后再试'));
      });
  });

  const { whiteListConfig: { USE_ITEM_GROUP = false } = {} } = useOrgConfigStateValue();

  const notSupportPreOrderPlatforms: General.PlatformUpperCase[] = ['LAZADA', 'ALIEXPRESS'];

  const radios = (
    <Radios>
      <Radio disabled={disabled} checked={type === 'ALL'} onChange={() => onTypeChange('ALL')}>
        {t('aide.全部商品')}
      </Radio>
      <Radio
        disabled={disabled}
        checked={type === 'SPECIFIC'}
        onChange={() => {
          onTypeChange('SPECIFIC');
          onSpecificItemsChange([]);
        }}
      >
        {t('aide.指定商品')}
      </Radio>
      {USE_ITEM_GROUP && (
        <Radio
          disabled={disabled}
          checked={type === 'ITEM_GROUP'}
          onChange={() => {
            onTypeChange('ITEM_GROUP');
            onSpecificItemsChange([]);
          }}
        >
          {t('指定商品组')}
        </Radio>
      )}
      {!notSupportPreOrderPlatforms.includes(platform) && (
        <Radio
          disabled={disabled}
          checked={preOrderTypeChecked}
          onChange={() => onTypeChange('PRE_ORDER')}
        >
          {t('aide.预售类型')}
        </Radio>
      )}
    </Radios>
  );

  const extra = (() => {
    if (preOrderTypeChecked) {
      return (
        <Extra>
          <Radio
            disabled={disabled}
            checked={type === 'PRE_ORDER'}
            onChange={() => onTypeChange('PRE_ORDER')}
          >
            {t('aide.预售')}
          </Radio>
          <Radio
            disabled={disabled}
            checked={type === 'IN_STOCK'}
            onChange={() => onTypeChange('IN_STOCK')}
          >
            {t('aide.现货')}
          </Radio>
        </Extra>
      );
    }

    if (type === 'ITEM_GROUP') {
      return <ItemGroupSelect {...props} />;
    }

    if (type !== 'SPECIFIC' || (!task && !stores)) {
      return null;
    }

    return (
      <Extra>
        {meta && (
          <ItemSelectModal
            visible={visible}
            platform={platform}
            region={region}
            stores={task?.stores || (stores as any)}
            skuEnable={skuEnable}
            value={meta}
            onChange={onSpecificItemsChange}
            onCancel={() => hide()}
          />
        )}
        {specificItems.length === 0 ? (
          <Button
            disabled={disabled}
            type="link"
            icon={<PlusOutlined />}
            onClick={() => show(specificItems)}
          >
            {t('aide.添加商品')}
          </Button>
        ) : (
          <>
            <Tag>{t('aide.已选x项', { x: specificItems.length })}</Tag>
            <Button disabled={disabled} type="link" onClick={() => show(specificItems)}>
              {t('aide.重新选择商品')}
            </Button>
          </>
        )}
      </Extra>
    );
  })();

  return (
    <FormField<FV['limitItem']>
      required
      name="limitItem"
      label={t('aide.限定商品')}
      validate={validate}
    >
      <Container>
        {radios}
        {extra}
      </Container>
    </FormField>
  );
}

function ItemGroupSelect(props: Pick<ItemLimitFieldProps, 'stores' | 'platform' | 'region'>) {
  const { stores, platform, region } = props;

  const { t } = useTranslate();

  const {
    field: { value: itemGroupIds, onChange: onItemGroupIdsChange },
  } = useSmartField<FV['limitItem']['itemGroupIds']>('limitItem.itemGroupIds');
  const {
    field: { value: specificItems, onChange: onSpecificItemsChange },
  } = useSmartField<FV['limitItem']['specificItems']>('limitItem.specificItems');

  const { loading, list, fetchList } = useCrud({
    readList: () =>
      queryItemGroupList({ itemGroupName: '' }).then((res) => {
        const list = res.data.itemGroup;

        const nextItemGroupIds = itemGroupIds?.filter((id) => {
          return list.some((_) => Number(_.id) === id);
        });

        if (nextItemGroupIds?.length !== itemGroupIds?.length) {
          onItemGroupIdsChange(nextItemGroupIds);
        }

        return list;
      }),
  });

  const refresh = () =>
    fetchList().catch(() => {
      message.error(t('phrase.获取数据失败，请稍后再试'));
    });

  useMount(refresh);

  const externalStoreMap = useExternalStoreMap(platform, region);
  const storeScopes = useMemo(
    () => stores2storeScopes(stores || [], platform, region, externalStoreMap),
    [stores, platform, region, externalStoreMap],
  );

  const [createGroupVisible, setCreateGroupVisible] = useState(false);

  const createDrawer = (
    <EditDrawer
      visible={createGroupVisible}
      onClose={() => setCreateGroupVisible(false)}
      onRefresh={refresh}
    />
  );

  const [selectVisible, setSelectVisible] = useState(false);

  const select = (
    <Select
      open={selectVisible}
      onDropdownVisibleChange={setSelectVisible}
      optionFilterProp="label"
      mode="multiple"
      placeholder={t('department.请选择x', { x: t('商品组') })}
      loading={loading}
      maxTagCount="responsive"
      maxTagPlaceholder={(values) => {
        return (
          <div>
            {t('等x个', { x: values.length })}
            <Preview
              content={values.map(({ value, label }) => {
                return (
                  <p key={value}>
                    <Tag>{label}</Tag>
                  </p>
                );
              })}
            />
          </div>
        );
      }}
      value={itemGroupIds}
      onChange={(value) =>
        value.length > 10 ? message.warn(t('至多选择x个', { x: 10 })) : onItemGroupIdsChange(value)
      }
      options={list?.map(({ id, name }) => ({ value: Number(id), label: name }))}
      dropdownRender={
        list?.length
          ? undefined
          : () => (
              <Empty
                description={[
                  <p>{t('无商品组')}</p>,
                  <Button
                    className="mb-2"
                    type="link"
                    icon={<PlusOutlined />}
                    onClick={() => {
                      setCreateGroupVisible(true);
                      setSelectVisible(false);
                    }}
                  >
                    {t('knowledge.新增x', { x: t('商品组') })}
                  </Button>,
                  createDrawer,
                ]}
              />
            )
      }
    />
  );

  const groupItemMap = useMemo(() => {
    if (!list || !itemGroupIds) {
      return {};
    }

    return itemGroupIds.reduce<Record<string, string[]>>((res, id) => {
      // eslint-disable-next-line eqeqeq
      const group = list.find((_) => _.id == id);

      if (!group) {
        return res;
      }

      return group.selectedItem.reduce((res, { itemInternalId, sku }) => {
        if (!res[itemInternalId]) {
          res[itemInternalId] = sku.map(({ skuId }) => skuId);
        } else {
          res[itemInternalId] = uniq([...res[itemInternalId], ...sku.map(({ skuId }) => skuId)]);
        }
        return res;
      }, res);
    }, {});
  }, [list, itemGroupIds]);

  const items = useMemo(() => {
    const map2list = (map: typeof groupItemMap) =>
      Object.entries(map).map(([itemInternalId, skuIds]) => {
        return {
          itemInternalId,
          skuIds,
        };
      });

    if (!specificItems) {
      return map2list(groupItemMap);
    }

    const newMap = specificItems.reduce(
      (res, { internalSpuId, skuIds, mode = 'INCLUDE' }) => {
        if (!internalSpuId) {
          return res;
        }

        if (mode === 'INCLUDE') {
          if (!res[internalSpuId]) {
            res[internalSpuId] = skuIds;
          } else {
            res[internalSpuId] = uniq([...res[internalSpuId], ...skuIds]);
          }
        } else if (res[internalSpuId]) {
          const nextSkuIds = res[internalSpuId].filter((id) => !skuIds.includes(id));
          if (nextSkuIds.length) {
            res[internalSpuId] = nextSkuIds;
          } else {
            delete res[internalSpuId];
          }
        }

        return res;
      },
      { ...groupItemMap },
    );

    return map2list(newMap);
  }, [specificItems, groupItemMap]);

  const {
    visible: previewVisible,
    show: showPreview,
    hide: hidePreview,
    update: updatePreview,
    meta: previewMeta,
  } = useModal<{
    validItems: ItemSelectV2.Item[];
    invalidItems: ItemSelectV2.Item[];
    updateTime?: number;
  }>();

  const {
    visible: editVisible,
    show: showEdit,
    hide: hideEdit,
    meta: editMeta,
  } = useModal<Omit<ItemSelectV2ComponentProps, 'visible'>>();

  const itemSelect = editMeta && <ItemSelectV2 {...editMeta} visible={editVisible} />;

  const preview = previewMeta && (
    <PreviewItemWrapper
      visible={previewVisible}
      onClose={() => hidePreview()}
      storeScopesLimit={storeScopes}
      items={previewMeta.validItems}
      updateTime={previewMeta.updateTime}
      footer={
        <Button
          onClick={() =>
            showEdit({
              storeType: 'ALL',
              storeScopes,
              value: previewMeta.validItems,
              onChange: (value) => {
                const items = [...value, ...previewMeta.invalidItems];
                const selectedItemIdMap: Partial<Record<number, true>> = {};

                const nextSpecificItems = items.reduce<Aide.SelectedItem[]>((res, item) => {
                  const { internalItemId, externalItemId: spuId, externalShopId, skuInfo } = item;
                  const skuIds = skuInfo.map(({ skuId }) => skuId);
                  const internalSpuId = Number(internalItemId);
                  const { [internalSpuId]: groupItemSkuIds } = groupItemMap;

                  if (!groupItemSkuIds) {
                    res.push({
                      spuId,
                      internalSpuId,
                      externalStoreId: externalShopId,
                      skuIds,
                      mode: 'INCLUDE',
                    });

                    return res;
                  }

                  selectedItemIdMap[internalSpuId] = true;

                  const includeSkuIds = skuIds.filter((id) =>
                    groupItemSkuIds.every((_) => _ !== id),
                  );
                  const excludeSkuIds = groupItemSkuIds.filter((id) =>
                    skuIds.every((_) => _ !== id),
                  );

                  if (includeSkuIds.length) {
                    res.push({
                      spuId,
                      internalSpuId,
                      externalStoreId: externalShopId,
                      skuIds: includeSkuIds,
                      mode: 'INCLUDE',
                    });
                  }

                  if (excludeSkuIds.length) {
                    res.push({
                      spuId,
                      internalSpuId,
                      externalStoreId: externalShopId,
                      skuIds: excludeSkuIds,
                      mode: 'EXCLUDE',
                    });
                  }

                  return res;
                }, []);

                const excludeItems = Object.entries(groupItemMap).reduce<[string, string[]][]>(
                  (res, cur) => {
                    const internalSpuId = Number(cur[0]);

                    if (selectedItemIdMap[internalSpuId]) {
                      return res;
                    }

                    res.push(cur);

                    return res;
                  },
                  [],
                );

                if (excludeItems.length === 0) {
                  onSpecificItemsChange(nextSpecificItems);

                  return;
                }

                getItemsByInternalIds(
                  excludeItems.map(([itemInternalId, skuIds]) => ({
                    itemInternalId,
                    skus: skuIds.map((skuId) => ({ skuId })),
                  })),
                )
                  .then((items) => {
                    nextSpecificItems.push(
                      ...items.map((item) => {
                        return {
                          spuId: item.externalItemId,
                          internalSpuId: Number(item.internalItemId),
                          externalStoreId: item.externalShopId,
                          skuIds: item.skuInfo.map(({ skuId }) => skuId),
                          mode: 'EXCLUDE' as const,
                        };
                      }),
                    );

                    onSpecificItemsChange(nextSpecificItems);
                  })
                  .catch(() => {
                    message.error(t('phrase.获取数据失败，请稍后再试'));
                  });
              },
              onCancel: () => hideEdit(),
            })
          }
        >
          {t('knowledge.编辑x', { x: t('商品') })}
        </Button>
      }
    />
  );

  const classifyItems = useCallback(
    (items: ItemSelectV2.Item[]) => {
      return items.reduce(
        (res, item) => {
          const { platform, region, internalStoreId } = item;

          const isValid = storeScopes.some((platformScope) => {
            return (
              platformScope.platform === platform &&
              platformScope.regionStoreScopes.some((regionScope) => {
                return (
                  (regionScope.region === 'all' && regionScope.storeCount > 0) ||
                  (regionScope.region === region &&
                    ((!regionScope.storeIds?.length && regionScope.storeCount > 0) ||
                      regionScope.storeIds?.includes(Number(internalStoreId))))
                );
              })
            );
          });

          if (isValid) {
            res.validItems.push(item);
          } else {
            res.invalidItems.push(item);
          }

          return res;
        },
        {
          validItems: [] as typeof items,
          invalidItems: [] as typeof items,
        },
      );
    },
    [storeScopes],
  );

  const onPreview = () => {
    return getItemsByInternalIds(
      items.map(({ itemInternalId, skuIds }) => ({
        itemInternalId,
        skus: skuIds.map((skuId) => ({
          skuId,
        })),
      })),
    )
      .then((items) => {
        showPreview({
          ...classifyItems(items),
          updateTime: itemGroupIds?.reduce<number | undefined>((res, id) => {
            const { itemUpdateTimeMs } = list?.find((_) => _.id === id) || {};
            if (!itemUpdateTimeMs) {
              return res;
            }
            if (!res) {
              return itemUpdateTimeMs;
            }
            return Math.max(res, itemUpdateTimeMs);
          }, undefined),
        });
      })
      .catch(() => {
        message.error(t('phrase.获取数据失败，请稍后再试'));
      });
  };

  const refsCurrent = {
    previewVisible,
    previewMeta,
    updatePreview,
  };
  const refs = useRef(refsCurrent);
  refs.current = refsCurrent;

  useEffect(() => {
    const { previewVisible, previewMeta } = refs.current;
    if (previewVisible && previewMeta) {
      getItemsByInternalIds(
        items.map(({ itemInternalId, skuIds }) => ({
          itemInternalId,
          skus: skuIds.map((skuId) => ({
            skuId,
          })),
        })),
      ).then((items) => {
        const { previewVisible, previewMeta, updatePreview } = refs.current;
        updatePreview(previewVisible, {
          ...previewMeta,
          ...classifyItems(items),
        });
      });
    }
  }, [items, classifyItems]);

  const addedSpecificItems = useMemo(
    () => specificItems?.filter((_) => _.mode !== 'EXCLUDE' && _.internalSpuId),
    [specificItems],
  );
  const [addedItems, setAddedItems] = useState<ItemSelectV2.Item[]>([]);
  useEffect(() => {
    if (!addedSpecificItems?.length) return;
    getItemsByInternalIds(
      addedSpecificItems.map(({ internalSpuId, skuIds }) => ({
        itemInternalId: String(internalSpuId),
        skus: skuIds.map((skuId) => ({
          skuId,
        })),
      })),
    )
      .then(setAddedItems)
      .catch(noop);
  }, [addedSpecificItems]);

  return (
    <ItemGroupSelectWrapper>
      {select}
      {addedSpecificItems?.length ? (
        <span>
          {t('其他x个商品', { x: addedSpecificItems.length })}
          <Preview
            content={
              <ItemList>
                {addedItems.map((item) => {
                  return <ItemRender key={item.internalItemId} item={item} foldState="unfold" />;
                })}
              </ItemList>
            }
          />
        </span>
      ) : null}
      {itemGroupIds?.length || addedSpecificItems?.length ? (
        <StatusButton type="link" onClick={onPreview}>
          {t('Entities.查看商品')}
        </StatusButton>
      ) : null}
      {preview}
      {itemSelect}
    </ItemGroupSelectWrapper>
  );
}

const ItemGroupSelectWrapper = styled.div`
  display: flex;
  align-items: center;
  > .ant-select {
    width: 388px;
  }
  > span {
    margin-left: 8px;
    padding: 2px 8px;
    background: var(--fett-bg-app);
    border-radius: 2px;
  }
`;

const ItemList = styled.div`
  max-width: 500px;
  max-height: 480px;
  margin: -12px -16px;
  padding: 14px;
  overflow: auto;
`;

export default ItemLimitField;
